import React, { useCallback, useMemo, useState } from 'react'
import type { TabsProps } from './Tabs.types'
import { TABS_DEMO_ITEMS } from './Tabs.types'
import './Tabs.css'

const Tabs: React.FC<TabsProps> = ({
  items = TABS_DEMO_ITEMS,
  value,
  defaultValue,
  orientation = 'horizontal',
  disabled = false,
  className = '',
  darkMode = false,
  previewActiveIndex,
  previewTabState,
  onChange,
}) => {
  const [internalValue, setInternalValue] = useState(
    defaultValue ?? items[0]?.id ?? '',
  )

  const isPreview = previewActiveIndex != null || previewTabState != null
  const isVertical = orientation === 'vertical'
  const resolvedValue =
    previewActiveIndex != null
      ? (items[previewActiveIndex]?.id ?? items[0]?.id ?? '')
      : (value ?? internalValue)

  const handleSelect = useCallback(
    (tabId: string) => {
      if (isPreview || disabled) {
        return
      }

      if (value === undefined) {
        setInternalValue(tabId)
      }

      onChange?.(tabId)
    },
    [disabled, isPreview, onChange, value],
  )

  const classNames = useMemo(
    () =>
      [
        'ds-tabs',
        isVertical ? 'ds-tabs--vertical' : 'ds-tabs--horizontal',
        darkMode ? 'ds-tabs--dark' : '',
        isPreview ? 'ds-tabs--preview' : '',
        disabled ? 'ds-tabs--disabled' : '',
        className,
      ]
        .filter(Boolean)
        .join(' '),
    [className, darkMode, disabled, isPreview, isVertical],
  )

  const getTabClassName = (isSelected: boolean): string =>
    [
      'ds-tabs__tab',
      isSelected ? 'ds-tabs__tab--selected' : 'ds-tabs__tab--default',
    ].join(' ')

  return (
    <div
      className={classNames}
      role="tablist"
      aria-orientation={isVertical ? 'vertical' : 'horizontal'}
    >
      {items.map((item) => {
        const isSelected = previewTabState
          ? previewTabState === 'selected'
          : item.id === resolvedValue

        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            className={getTabClassName(isSelected)}
            aria-selected={isSelected}
            disabled={disabled || isPreview}
            tabIndex={isPreview ? -1 : isSelected ? 0 : -1}
            onClick={() => handleSelect(item.id)}
          >
            {item.label}
          </button>
        )
      })}
    </div>
  )
}

export default Tabs
